import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  PaperAirplaneIcon,
  FunnelIcon,
  QrCodeIcon,
  FolderIcon, // Import FolderIcon
  FolderPlusIcon // Import FolderPlusIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import InvoiceViewModalNew from '../components/InvoiceViewModalNew';
// import QRCodeGenerator from '../components/QRCodeGenerator'; // Removed as unused
import {
  formatInvoiceDate,
  formatCurrency,
  canEditInvoice,
  canDeleteInvoice,
  canValidateInvoice,
  canSubmitInvoice,
  getInvoiceButtonStates,
  searchInvoices,
  filterInvoicesByStatus
} from '../utils/invoiceUtils';

const InvoiceList = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [jsonModal, setJsonModal] = useState({ show: false, title: '', content: '', isDevelopmentMode: false });

  // Folder management states
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('all'); // 'all', 'unarchived', or folder.id
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showMoveInvoiceModal, setShowMoveInvoiceModal] = useState(false);
  const [, setShowEditFolderModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [showFolderManagementModal, setShowFolderManagementModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null); // Folder being edited
  const [editedFolderName, setEditedFolderName] = useState(''); // Name for editing
  const [folderToDelete, setFolderToDelete] = useState(null); // Folder being deleted
  const [invoiceToMove, setInvoiceToMove] = useState(null); // Invoice being moved
  const [targetFolderId, setTargetFolderId] = useState(''); // Target folder for moving invoice
  const [folderActionLoading, setFolderActionLoading] = useState({
    create: false,
    update: false,
    delete: false,
    move: false,
  });

  const itemsPerPage = 15; // Display 15 invoices per page

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.invoices.getAll({
        page: currentPage,
        limit: itemsPerPage,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sortBy,
        sortOrder,
        folderId: selectedFolder === 'all' ? undefined : (selectedFolder === 'unarchived' ? 'unarchived' : parseInt(selectedFolder))
      });

      const { invoices = [], pagination = {} } = response.data || {};
      
      // Debug: Log the invoices to check if they have batch_id
      console.log('🔍 FRONTEND: Received invoices:', invoices);
      if (invoices.length > 0) {
        console.log('🔍 FRONTEND: First invoice batch_id:', invoices[0].batch_id);
      }
      
      setInvoices(invoices);
      setTotalPages(pagination.pages || 1);
      setTotalInvoices(pagination.total || 0);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, sortBy, sortOrder, selectedFolder]);

  const loadFolders = useCallback(async () => {
    try {
      const response = await api.folders.getAll();
      setFolders(response.data.folders);
    } catch (error) {
      console.error('Failed to load folders:', error);
      toast.error('Failed to load folders');
    }
  }, []);

  useEffect(() => {
    loadInvoices();
    loadFolders(); // Load folders when component mounts
  }, [loadInvoices, loadFolders]);

  const handleSearch = (text) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleFolderFilter = (folderId) => {
    setSelectedFolder(folderId);
    setCurrentPage(1);
  };


  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setCurrentPage(1);
  };

  const handleViewInvoice = async (invoice) => {
    try {
      console.log('🔍 VIEW CLICKED - Invoice ID:', invoice.id, 'Invoice Number:', invoice.invoice_number);
      console.log('🔍 Full invoice object:', invoice);

      // Clear previous invoice first to force re-render
      setSelectedInvoice(null);
      setShowViewModal(false);

      console.log('📡 Making API call to load invoice:', invoice.id);
      const response = await api.invoices.getById(invoice.id);
      console.log('✅ API Response received for invoice:', response.data.invoice.id, response.data.invoice.invoice_number);

      setSelectedInvoice(response.data.invoice);
      setShowViewModal(true);
      console.log('🎯 Modal should now show invoice:', response.data.invoice.id);
    } catch (error) {
      console.error('❌ Failed to load invoice details:', error);
      toast.error('Failed to load invoice details');
    }
  };

  const handleEditInvoice = async (invoice) => {
    if (!canEditInvoice(invoice)) {
      toast.error('This invoice cannot be edited');
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [`edit_${invoice.id}`]: true }));
      
      // Load invoice data for canvas-based editing
      console.log('🎨 Loading invoice for canvas-based editing:', invoice.id);
      
      const response = await api.invoices.getById(invoice.id);
      const invoiceData = response.data.invoice;
      
      console.log('📄 Invoice data loaded for canvas editing:', invoiceData);
      
      // Transform invoice data to canvas-compatible format
      const canvasFormData = await transformInvoiceToCanvasFormat(invoiceData);
      
      // Navigate to canvas form with pre-populated data
      navigate('/app/invoice/canvas', { 
        state: { 
          editMode: true,
          invoiceId: invoice.id,
          formData: canvasFormData,
          originalInvoice: invoiceData
        }
      });
      
    } catch (error) {
      console.error('❌ Failed to load invoice for canvas editing:', error);
      toast.error('Failed to load invoice for editing');
    } finally {
      setActionLoading(prev => ({ ...prev, [`edit_${invoice.id}`]: false }));
    }
  };

  // Transform invoice data to canvas-compatible format
  const transformInvoiceToCanvasFormat = async (invoice) => {
    try {
      // Load scenarios to get the correct scenario configuration
      const scenariosResponse = await api.canvas.getScenarios();
      const scenarios = scenariosResponse.data.data || [];
      
      // Find the scenario for this invoice
      const scenario = scenarios.find(s => 
        (s.scenario_id || s.scenarioId || s.id) === invoice.scenario_id
      );
      
      if (!scenario) {
        console.warn('⚠️ No scenario found for invoice scenario_id:', invoice.scenario_id);
      }
      
      // Use JSON data if available, otherwise construct from database fields
      let formData;
      
      if (invoice.json_data) {
        try {
          formData = JSON.parse(invoice.json_data);
          console.log('📄 Using JSON data for canvas form:', formData);
        } catch (error) {
          console.warn('⚠️ Failed to parse JSON data, constructing from database fields');
          formData = null;
        }
      }
      
      if (!formData) {
        // Construct canvas form data from database fields
        formData = {
          scenarioId: invoice.scenario_id,
          invoiceType: invoice.invoice_type || 'Sale Invoice',
          invoiceDate: invoice.invoice_date?.split('T')[0] || new Date().toISOString().split('T')[0],
          
          // Seller information
          sellerNTNCNIC: invoice.seller_ntn_cnic || '',
          sellerBusinessName: invoice.seller_business_name || '',
          sellerProvince: invoice.seller_province || '',
          sellerAddress: invoice.seller_address || '',
          
          // Buyer information
          buyerNTNCNIC: invoice.buyer_ntn_cnic || '',
          buyerBusinessName: invoice.buyer_business_name || '',
          buyerProvince: invoice.buyer_province || '',
          buyerAddress: invoice.buyer_address || '',
          buyerRegistrationType: invoice.buyer_registration_type || '',
          
          // Invoice details
          invoiceRefNo: invoice.invoice_ref_no || '',
          
          // Items - transform database items to canvas format
          items: invoice.items?.map(item => ({
            hsCode: item.hs_code || '',
            productDescription: item.product_description || '',
            rate: scenario ? (scenario.tax_rate || scenario.taxRate || scenario.rate || '') : (item.rate || ''),
            uoM: item.uom || '',
            quantity: parseFloat(item.quantity) || 1,
            
            // Use scenario-based saleType instead of database value
            saleType: scenario ? (scenario.sale_type || scenario.saleType || '') : (item.sale_type || ''),
            
            // Calculated values - will be recalculated by canvas
            unitPriceExcludingTax: parseFloat(item.unit_price_excluding_tax) || 0,
            totalValues: parseFloat(item.total_values) || 0,
            valueSalesExcludingST: parseFloat(item.value_sales_excluding_st) || 0,
            fixedNotifiedValueOrRetailPrice: parseFloat(item.fixed_notified_value_or_retail_price) || 0,
            salesTaxApplicable: parseFloat(item.sales_tax_applicable) || 0,
            salesTaxWithheldAtSource: parseFloat(item.sales_tax_withheld_at_source) || 0,
            extraTax: parseFloat(item.extra_tax) || 0,
            furtherTax: parseFloat(item.further_tax) || 0,
            fedPayable: parseFloat(item.fed_payable) || 0,
            discount: parseFloat(item.discount) || 0,
            
            // Additional fields
            sroScheduleNo: item.sro_schedule_no || '',
            sroItemSerialNo: item.sro_item_serial_no || '',
            taxCalculationType: item.tax_calculation_type || 'exclusive'
          })) || []
        };
        
        console.log('🔧 Constructed canvas form data from database fields:', formData);
      }
      
      // Ensure saleType is correct based on scenario (fix any incorrect values)
      if (scenario && formData.items) {
        const correctSaleType = scenario.sale_type || scenario.saleType || '';
        const correctRate = scenario.tax_rate || scenario.taxRate || scenario.rate || '';
        
        console.log('🔧 Correcting saleType and rate based on scenario:', { correctSaleType, correctRate });
        
        formData.items = formData.items.map(item => ({
          ...item,
          saleType: correctSaleType,
          rate: correctRate
        }));
      }
      
      return formData;
      
    } catch (error) {
      console.error('❌ Error transforming invoice to canvas format:', error);
      throw error;
    }
  };

  // Show JSON response modal
  const showJSONModal = (title, content, isDevelopmentMode = false) => {
    setJsonModal({
      show: true,
      title,
      content,
      isDevelopmentMode
    });
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!canDeleteInvoice(invoice)) {
      toast.error('This invoice cannot be deleted');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(prev => ({ ...prev, [`delete_${invoice.id}`]: true }));
      await api.invoices.delete(invoice.id);
      toast.success('Invoice deleted successfully');
      loadInvoices();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error(error.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setActionLoading(prev => ({ ...prev, [`delete_${invoice.id}`]: false }));
    }
  };

  const handleValidateInvoice = async (invoice) => {
    // Emergency debugging
    if (window.simpleDebugger) {
      window.simpleDebugger.log('🚀 VALIDATE INVOICE STARTED', { invoiceId: invoice.id, status: invoice.status });
    }
    console.log('🚀 VALIDATE INVOICE STARTED:', invoice);

    if (!canValidateInvoice(invoice)) {
      if (window.simpleDebugger) {
        window.simpleDebugger.log('❌ VALIDATION BLOCKED - Cannot validate invoice', invoice);
      }
      toast.error('This invoice cannot be validated');
      return;
    }

    try {
      if (window.simpleDebugger) {
        window.simpleDebugger.log('📤 SENDING VALIDATION REQUEST', { invoiceId: invoice.id });
      }
      setActionLoading(prev => ({ ...prev, [`validate_${invoice.id}`]: true }));

      // Use FBR Validate API with raw JSON from invoice.json_data
      const invoiceDetails = await api.invoices.getById(invoice.id);
      const invoiceData = invoiceDetails.data?.invoice?.json_data || {};
      const response = await api.fbrValidate.validate({ invoiceData, invoiceId: invoice.id });

      if (window.simpleDebugger) {
        window.simpleDebugger.log('📥 VALIDATION RESPONSE RECEIVED', response);
      }

      // Show detailed response in a modal (use validation object)
      const responseData = response.data;
      const isDevelopmentMode = false;

      // Create a formatted JSON display
      const jsonResponse = JSON.stringify(responseData.validation || responseData, null, 2);

      // Show the JSON modal immediately
      showJSONModal('FBR Validation Response', jsonResponse, isDevelopmentMode);

      // Show messages based on status with detailed FBR error information
      const ok = response?.data?.success !== false && response?.status === 200;
      if (ok) {
        toast.success('Invoice validated successfully');
      } else {
        // Extract detailed FBR error information
        const responseData = response?.data;
        const validation = responseData?.validation;
        const validationResponse = validation?.validationResponse;
        
        // Get the main error message
        let errorMessage = responseData?.message || 'Invoice validation failed';
        
        // Get detailed error information from FBR response
        const statusCode = validationResponse?.statusCode;
        const statusMessage = validationResponse?.statusMessage;
        const errors = validationResponse?.errors;
        
        // Build detailed error message
        let detailedError = errorMessage;
        
        if (statusCode && statusCode !== '00') {
          detailedError += `\n\nFBR Status Code: ${statusCode}`;
        }
        
        if (statusMessage) {
          detailedError += `\nFBR Status: ${statusMessage}`;
        }
        
        if (errors && Array.isArray(errors) && errors.length > 0) {
          detailedError += '\n\nFBR Errors:';
          errors.forEach((error, index) => {
            if (error.description) {
              detailedError += `\n${index + 1}. ${error.description}`;
            }
            if (error.field) {
              detailedError += ` (Field: ${error.field})`;
            }
            if (error.code) {
              detailedError += ` (Code: ${error.code})`;
            }
          });
        }
        
        console.error('❌ FBR Validation Failed:', {
          statusCode,
          statusMessage,
          errors,
          fullResponse: responseData
        });
        
        // Show detailed error in toast (with longer duration for reading)
        toast.error(detailedError, {
          duration: 8000,
          style: {
            maxWidth: '500px',
            whiteSpace: 'pre-line'
          }
        });
      }

      loadInvoices();
    } catch (error) {
      if (window.simpleDebugger) {
        window.simpleDebugger.log('❌ VALIDATION ERROR', error);
      }
      console.error('Failed to validate invoice:', error);
      
      // Extract detailed error information from API response
      const errorResponse = error.response?.data;
      let errorMessage = 'Failed to validate invoice';
      
      if (errorResponse) {
        // Get the main error message
        errorMessage = errorResponse.message || errorMessage;
        
        // Get FBR validation details if available
        const validation = errorResponse.validation;
        const validationResponse = validation?.validationResponse;
        
        if (validationResponse) {
          const statusCode = validationResponse.statusCode;
          const statusMessage = validationResponse.statusMessage;
          const errors = validationResponse.errors;
          
          let detailedError = errorMessage;
          
          if (statusCode && statusCode !== '00') {
            detailedError += `\n\nFBR Status Code: ${statusCode}`;
          }
          
          if (statusMessage) {
            detailedError += `\nFBR Status: ${statusMessage}`;
          }
          
          if (errors && Array.isArray(errors) && errors.length > 0) {
            detailedError += '\n\nFBR Errors:';
            errors.forEach((err, index) => {
              if (err.description) {
                detailedError += `\n${index + 1}. ${err.description}`;
              }
              if (err.field) {
                detailedError += ` (Field: ${err.field})`;
              }
              if (err.code) {
                detailedError += ` (Code: ${err.code})`;
              }
            });
          }
          
          errorMessage = detailedError;
        }
      }
      
      toast.error(errorMessage, {
        duration: 8000,
        style: {
          maxWidth: '500px',
          whiteSpace: 'pre-line'
        }
      });
    } finally {
      if (window.simpleDebugger) {
        window.simpleDebugger.log('🏁 VALIDATION COMPLETED', { invoiceId: invoice.id });
      }
      setActionLoading(prev => ({ ...prev, [`validate_${invoice.id}`]: false }));
    }
  };

  const handleSubmitInvoice = async (invoice) => {
    // Emergency debugging
    if (window.simpleDebugger) {
      window.simpleDebugger.log('🚀 SUBMIT INVOICE STARTED', { invoiceId: invoice.id, status: invoice.status });
    }
    console.log('🚀 SUBMIT INVOICE STARTED:', invoice);

    if (!canSubmitInvoice(invoice)) {
      if (window.simpleDebugger) {
        window.simpleDebugger.log('❌ SUBMISSION BLOCKED - Cannot submit invoice', invoice);
      }
      toast.error('This invoice cannot be submitted');
      return;
    }

    const isAutoValidate = invoice.status === 'Pending';
    const confirmMessage = isAutoValidate
      ? 'This invoice will be validated and then submitted to FBR. This action cannot be undone. Continue?'
      : 'Are you sure you want to submit this invoice to FBR? This action cannot be undone.';

    if (!window.confirm(confirmMessage)) {
      if (window.simpleDebugger) {
        window.simpleDebugger.log('❌ SUBMISSION CANCELLED BY USER', { invoiceId: invoice.id });
      }
      return;
    }

    try {
      if (window.simpleDebugger) {
        window.simpleDebugger.log('📤 SENDING SUBMISSION REQUEST', { invoiceId: invoice.id, isAutoValidate });
      }
      setActionLoading(prev => ({ ...prev, [`submit_${invoice.id}`]: true }));

      if (isAutoValidate) {
        toast.info('Validating and submitting invoice...', { autoClose: 2000 });
      }

      // Use FBR Submit API with raw JSON and invoiceId; validateFirst=true for auto-validate
      const invoiceDetails = await api.invoices.getById(invoice.id);
      const invoiceData = invoiceDetails.data?.invoice?.json_data || {};
      const response = await api.fbrValidate.submit({ invoiceData, invoiceId: invoice.id, validateFirst: true });

      if (window.simpleDebugger) {
        window.simpleDebugger.log('📥 SUBMISSION RESPONSE RECEIVED', response);
      }

      // Show detailed response in a modal (use submission object)
      const responseData = response.data;
      const isDevelopmentMode = false;

      // Create a formatted JSON display
      const jsonResponse = JSON.stringify(responseData.submission || responseData, null, 2);

      // Show the JSON modal immediately
      showJSONModal('FBR Submission Response', jsonResponse, isDevelopmentMode);

      // Show messages based on status with detailed FBR error information
      const ok = response?.data?.success !== false && response?.status === 200;
      if (ok) {
        toast.success('Invoice submitted to FBR successfully');
      } else {
        // Extract detailed FBR error information
        const responseData = response?.data;
        const submission = responseData?.submission;
        const submissionResponse = submission?.submissionResponse;
        const validation = responseData?.validation;
        const validationResponse = validation?.validationResponse;
        
        // Get the main error message
        let errorMessage = responseData?.message || 'Invoice submission failed';
        
        // Build detailed error message
        let detailedError = errorMessage;
        
        // Check for submission errors first
        if (submissionResponse) {
          const statusCode = submissionResponse.statusCode;
          const statusMessage = submissionResponse.statusMessage;
          const errors = submissionResponse.errors;
          
          if (statusCode && statusCode !== '00') {
            detailedError += `\n\nFBR Submission Status Code: ${statusCode}`;
          }
          
          if (statusMessage) {
            detailedError += `\nFBR Submission Status: ${statusMessage}`;
          }
          
          if (errors && Array.isArray(errors) && errors.length > 0) {
            detailedError += '\n\nFBR Submission Errors:';
            errors.forEach((error, index) => {
              if (error.description) {
                detailedError += `\n${index + 1}. ${error.description}`;
              }
              if (error.field) {
                detailedError += ` (Field: ${error.field})`;
              }
              if (error.code) {
                detailedError += ` (Code: ${error.code})`;
              }
            });
          }
        }
        
        // Check for validation errors (if validateFirst was true)
        if (validationResponse && validationResponse.statusCode !== '00') {
          const statusCode = validationResponse.statusCode;
          const statusMessage = validationResponse.statusMessage;
          const errors = validationResponse.errors;
          
          if (statusCode) {
            detailedError += `\n\nFBR Validation Status Code: ${statusCode}`;
          }
          
          if (statusMessage) {
            detailedError += `\nFBR Validation Status: ${statusMessage}`;
          }
          
          if (errors && Array.isArray(errors) && errors.length > 0) {
            detailedError += '\n\nFBR Validation Errors:';
            errors.forEach((error, index) => {
              if (error.description) {
                detailedError += `\n${index + 1}. ${error.description}`;
              }
              if (error.field) {
                detailedError += ` (Field: ${error.field})`;
              }
              if (error.code) {
                detailedError += ` (Code: ${error.code})`;
              }
            });
          }
        }
        
        console.error('❌ FBR Submission Failed:', {
          submissionResponse,
          validationResponse,
          fullResponse: responseData
        });
        
        // Show detailed error in toast (with longer duration for reading)
        toast.error(detailedError, {
          duration: 10000,
          style: {
            maxWidth: '500px',
            whiteSpace: 'pre-line'
          }
        });
      }

      loadInvoices();
    } catch (error) {
      if (window.simpleDebugger) {
        window.simpleDebugger.log('❌ SUBMISSION ERROR', error);
      }
      console.error('Failed to submit invoice:', error);
      
      // Extract detailed error information from API response
      const errorResponse = error.response?.data;
      let errorMessage = 'Failed to submit invoice';
      
      if (errorResponse) {
        // Get the main error message
        errorMessage = errorResponse.message || errorMessage;
        
        // Get FBR submission/validation details if available
        const submission = errorResponse.submission;
        const submissionResponse = submission?.submissionResponse;
        const validation = errorResponse.validation;
        const validationResponse = validation?.validationResponse;
        
        let detailedError = errorMessage;
        
        // Check for submission errors
        if (submissionResponse) {
          const statusCode = submissionResponse.statusCode;
          const statusMessage = submissionResponse.statusMessage;
          const errors = submissionResponse.errors;
          
          if (statusCode && statusCode !== '00') {
            detailedError += `\n\nFBR Submission Status Code: ${statusCode}`;
          }
          
          if (statusMessage) {
            detailedError += `\nFBR Submission Status: ${statusMessage}`;
          }
          
          if (errors && Array.isArray(errors) && errors.length > 0) {
            detailedError += '\n\nFBR Submission Errors:';
            errors.forEach((err, index) => {
              if (err.description) {
                detailedError += `\n${index + 1}. ${err.description}`;
              }
              if (err.field) {
                detailedError += ` (Field: ${err.field})`;
              }
              if (err.code) {
                detailedError += ` (Code: ${err.code})`;
              }
            });
          }
        }
        
        // Check for validation errors
        if (validationResponse && validationResponse.statusCode !== '00') {
          const statusCode = validationResponse.statusCode;
          const statusMessage = validationResponse.statusMessage;
          const errors = validationResponse.errors;
          
          if (statusCode) {
            detailedError += `\n\nFBR Validation Status Code: ${statusCode}`;
          }
          
          if (statusMessage) {
            detailedError += `\nFBR Validation Status: ${statusMessage}`;
          }
          
          if (errors && Array.isArray(errors) && errors.length > 0) {
            detailedError += '\n\nFBR Validation Errors:';
            errors.forEach((err, index) => {
              if (err.description) {
                detailedError += `\n${index + 1}. ${err.description}`;
              }
              if (err.field) {
                detailedError += ` (Field: ${err.field})`;
              }
              if (err.code) {
                detailedError += ` (Code: ${err.code})`;
              }
            });
          }
        }
        
        errorMessage = detailedError;
      }
      
      toast.error(errorMessage, {
        duration: 10000,
        style: {
          maxWidth: '500px',
          whiteSpace: 'pre-line'
        }
      });
    } finally {
      if (window.simpleDebugger) {
        window.simpleDebugger.log('🏁 SUBMISSION COMPLETED', { invoiceId: invoice.id });
      }
      setActionLoading(prev => ({ ...prev, [`submit_${invoice.id}`]: false }));
    }
  };

  const handleGenerateQRCode = async (invoice) => {
    try {
      setActionLoading(prev => ({ ...prev, [`qr_${invoice.id}`]: true }));
      const response = await api.invoices.generateQRCode(invoice.id, { type: 'invoice' });

      // Create a download link for the QR code image
      const link = document.createElement('a');
      link.href = response.data.qr_image || response.data.qr_code; // Use qr_image for download, fallback to qr_code
      link.download = `invoice-${invoice.id}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('QR code downloaded successfully');
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      toast.error('Failed to generate QR code: ' + error.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [`qr_${invoice.id}`]: false }));
    }
  };

  // Filter and search invoices
  const filteredInvoices = searchInvoices(
    filterInvoicesByStatus(invoices, statusFilter === 'all' ? null : statusFilter),
    searchText
  );

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Validated', label: 'Validated' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Error', label: 'Error' }
  ];

  const handleCreateFolder = async () => {
    try {
      setFolderActionLoading(prev => ({ ...prev, create: true }));
      await api.folders.create({ name: newFolderName });
      toast.success('Folder created successfully!');
      setNewFolderName('');
      setShowCreateFolderModal(false);
      loadFolders();
    } catch (error) {
      console.error('Failed to create folder:', error);
      toast.error(error.response?.data?.message || 'Failed to create folder');
    } finally {
      setFolderActionLoading(prev => ({ ...prev, create: false }));
    }
  };

  const handleEditFolderClick = (folder) => {
    setEditingFolder(folder);
    setEditedFolderName(folder.name);
    setShowEditFolderModal(true);
  };

  const handleUpdateFolder = async (folderId) => {
    try {
      setFolderActionLoading(prev => ({ ...prev, update: true }));
      await api.folders.update(folderId, { name: editedFolderName });
      toast.success('Folder updated successfully!');
      setEditingFolder(null);
      setEditedFolderName('');
      setShowEditFolderModal(false);
      loadFolders();
    } catch (error) {
      console.error('Failed to update folder:', error);
      toast.error(error.response?.data?.message || 'Failed to update folder');
    } finally {
      setFolderActionLoading(prev => ({ ...prev, update: false }));
    }
  };

  const handleDeleteFolderClick = (folder) => {
    setFolderToDelete(folder);
    setShowDeleteFolderModal(true);
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      setFolderActionLoading(prev => ({ ...prev, delete: true }));
      await api.folders.delete(folderId);
      toast.success('Folder deleted successfully!');
      setFolderToDelete(null);
      setShowDeleteFolderModal(false);
      loadFolders();
      loadInvoices(); // Reload invoices as some might have moved to unarchived
    } catch (error) {
      console.error('Failed to delete folder:', error);
      toast.error(error.response?.data?.message || 'Failed to delete folder');
    } finally {
      setFolderActionLoading(prev => ({ ...prev, delete: false }));
    }
  };

  const handleMoveInvoiceClick = (invoice) => {
    setInvoiceToMove(invoice);
    setTargetFolderId(invoice.folder_id || 'unarchived'); // Pre-select current folder or 'unarchived'
    setShowMoveInvoiceModal(true);
  };

  const handleMoveInvoice = async () => {
    if (!invoiceToMove || !targetFolderId) {
      toast.error('Please select an invoice and a target folder.');
      return;
    }

    try {
      setFolderActionLoading(prev => ({ ...prev, move: true }));
      const folderId = targetFolderId === 'unarchived' ? null : parseInt(targetFolderId);
      await api.invoices.move(invoiceToMove.id, { folderId });
      toast.success('Invoice moved successfully!');
      setShowMoveInvoiceModal(false);
      setInvoiceToMove(null);
      setTargetFolderId('');
      loadInvoices(); // Reload invoices to reflect the change
    } catch (error) {
      console.error('Failed to move invoice:', error);
      toast.error(error.response?.data?.message || 'Failed to move invoice');
    } finally {
      setFolderActionLoading(prev => ({ ...prev, move: false }));
    }
  };
  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Invoices</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage your invoices and track their status
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowCreateFolderModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FolderPlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                New Folder
              </button>
              <button
                onClick={() => setShowFolderManagementModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FolderIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Manage Folders
              </button>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowCreateFolderModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FolderPlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                New Folder
              </button>
              <button
                onClick={() => setShowFolderManagementModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FolderIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Manage Folders
              </button>
            </div>
          </div>

          {/* Folder Filter */}
          <div className="sm:w-48">
            <div className="relative">
              <FolderIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={selectedFolder}
                onChange={(e) => handleFolderFilter(e.target.value)}
                className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
              >
                <option value="all">All Invoices</option>
                <option value="unarchived">Unarchived</option>
                {folders.map(folder => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {/* Filters and Search */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-48">
              <div className="relative">
                <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Folder Filter */}
            <div className="sm:w-48">
              <div className="relative">
                <FolderIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={selectedFolder}
                  onChange={(e) => handleFolderFilter(e.target.value)}
                  className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                >
                  <option value="all">All Invoices</option>
                  <option value="unarchived">Unarchived</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="mt-3 text-sm text-gray-600">
            {filteredInvoices.length === totalInvoices ? (
              <>
                {totalInvoices} invoices total
                {totalPages > 1 && (
                  <span className="text-gray-500"> • {itemsPerPage} per page • {totalPages} pages</span>
                )}
              </>
            ) : (
              <>
                Showing {filteredInvoices.length} of {totalInvoices} invoices
                {totalPages > 1 && (
                  <span className="text-gray-500"> • {itemsPerPage} per page</span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Invoice Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading invoices...</span>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No invoices found</p>
              <p className="text-sm text-gray-400 mt-2">
                {searchText || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Create your first invoice to get started'
                }
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('invoice_date')}
                  >
                    Date
                    {sortBy === 'invoice_date' && (
                      <span className="ml-1">{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyer
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    Status
                    {sortBy === 'status' && (
                      <span className="ml-1">{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    FBR Invoice #
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('total_amount')}
                  >
                    Amount
                    {sortBy === 'total_amount' && (
                      <span className="ml-1">{sortOrder === 'ASC' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatInvoiceDate(invoice.invoice_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {invoice.fbr_invoice_number ? (
                          <span className="font-medium">#{invoice.fbr_invoice_number}</span>
                        ) : invoice.invoice_number ? (
                          <span className="font-medium">#{invoice.invoice_number}</span>
                        ) : (
                          <span className="text-gray-500">Draft #{invoice.id}</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {invoice.invoice_type} • {invoice.item_count || 0} items
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {invoice.buyer_business_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={invoice.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invoice.fbr_invoice_number ? (
                        <div className="text-sm">
                          <div className="font-mono font-bold text-blue-600 truncate max-w-40" title={invoice.fbr_invoice_number}>
                            {invoice.fbr_invoice_number}
                          </div>
                          <div className="text-xs text-gray-500">FBR Submitted</div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">
                          <div className="text-xs">Not submitted</div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(invoice.total_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {/* View Button */}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewInvoice(invoice); }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="View Invoice"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>

                        {/* Move to Folder Button */}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMoveInvoiceClick(invoice); }}
                          className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50"
                          title="Move to Folder"
                        >
                          <FolderIcon className="w-4 h-4" />
                        </button>

                        {/* Edit Button - Canvas Based */}
                        {canEditInvoice(invoice) && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditInvoice(invoice); }}
                            disabled={actionLoading[`edit_${invoice.id}`]}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Edit Invoice (Canvas Based)"
                          >
                            {actionLoading[`edit_${invoice.id}`] ? (
                              <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <PencilIcon className="w-4 h-4" />
                            )}
                          </button>
                        )}

                        {/* Delete Button */}
                        {canDeleteInvoice(invoice) && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteInvoice(invoice); }}
                            disabled={actionLoading[`delete_${invoice.id}`]}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 disabled:opacity-50"
                            title="Delete Invoice"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}

                        {(() => {
                          const buttonStates = getInvoiceButtonStates(invoice);

                          return (
                            <>
                              {/* Validate Button */}
                              {buttonStates.validate.show && (
                                <div className="relative">
                                  <button
                                    onClick={() => buttonStates.validate.enabled ? handleValidateInvoice(invoice) : null}
                                    disabled={!buttonStates.validate.enabled || actionLoading[`validate_${invoice.id}`]}
                                    className={`${buttonStates.validate.className} ${!buttonStates.validate.enabled || actionLoading[`validate_${invoice.id}`] ? 'disabled:opacity-50' : ''}`}
                                    title={buttonStates.validate.title}
                                  >
                                    <CheckIcon className="w-4 h-4" />
                                  </button>
                                  {/* Status indicator dot */}
                                  {!buttonStates.validate.enabled && (
                                    <div className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${
                                      invoice.status === 'Validated' ? 'bg-gray-400' :
                                      invoice.status === 'Submitted' ? 'bg-green-400' : 'bg-gray-300'
                                    }`}></div>
                                  )}
                                </div>
                              )}

                              {/* Submit Button */}
                              {buttonStates.submit.show && (
                                <div className="relative">
                                  <button
                                    onClick={() => buttonStates.submit.enabled ? handleSubmitInvoice(invoice) : null}
                                    disabled={!buttonStates.submit.enabled || actionLoading[`submit_${invoice.id}`]}
                                    className={`${buttonStates.submit.className} ${!buttonStates.submit.enabled || actionLoading[`submit_${invoice.id}`] ? 'disabled:opacity-50' : ''}`}
                                    title={buttonStates.submit.title}
                                  >
                                    <PaperAirplaneIcon className="w-4 h-4" />
                                  </button>
                                  {/* Status indicator dot */}
                                  {!buttonStates.submit.enabled && invoice.status === 'Submitted' && (
                                    <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-green-400"></div>
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })()}

                        {/* QR Code Button */}
                        {(invoice.status === 'Submitted' || invoice.status === 'Validated') && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleGenerateQRCode(invoice); }}
                            disabled={actionLoading[`qr_${invoice.id}`]}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 disabled:opacity-50"
                            title="Generate QR Code"
                          >
                            <QrCodeIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Enhanced Pagination */}
        {!loading && filteredInvoices.length > 0 && totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              {/* Pagination Info */}
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredInvoices.length)}</span> of{' '}
                <span className="font-medium">{filteredInvoices.length}</span> invoices
                {filteredInvoices.length !== totalInvoices && (
                  <span className="text-gray-500"> (filtered from {totalInvoices} total)</span>
                )}
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                {/* Previous Button */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentPage(prev => Math.max(1, prev - 1)); }}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                    // Adjust start page if we're near the end
                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                    // Add first page and ellipsis if needed
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => setCurrentPage(1)}
                          className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis1" className="px-2 py-2 text-sm text-gray-500">
                            ...
                          </span>
                        );
                      }
                    }

                    // Add visible page numbers
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md ${
                            i === currentPage
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    // Add last page and ellipsis if needed
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis2" className="px-2 py-2 text-sm text-gray-500">
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                          className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                {/* Next Button */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentPage(prev => Math.min(totalPages, prev + 1)); }}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice View Modal */}
      <InvoiceViewModalNew
        key={selectedInvoice?.id || 'no-invoice'}
        invoice={selectedInvoice}
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedInvoice(null);
        }}
      />

      {/* Create Folder Modal */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b bg-blue-50 border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800">Create New Folder</h3>
            </div>
            <div className="p-6">
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder Name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateFolderModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim() || folderActionLoading.create}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {folderActionLoading.create ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move Invoice Modal */}
      {showMoveInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b bg-purple-50 border-purple-200">
              <h3 className="text-lg font-semibold text-purple-800">Move Invoice to Folder</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">
                Move invoice <span className="font-medium">#{invoiceToMove?.invoice_number || invoiceToMove?.id}</span> to:
              </p>
              <select
                value={targetFolderId}
                onChange={(e) => setTargetFolderId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Select a folder</option>
                <option value="unarchived">Unarchived</option>
                {folders.map(folder => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setShowMoveInvoiceModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMoveInvoice}
                  disabled={!targetFolderId || folderActionLoading.move}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {folderActionLoading.move ? 'Moving...' : 'Move'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Folder Management Modal */}
      {showFolderManagementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b bg-indigo-50 border-indigo-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-indigo-800">Manage Folders</h3>
              <button
                onClick={() => setShowFolderManagementModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto max-h-[70vh]">
              <div className="mb-4 flex items-center space-x-2">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="New Folder Name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim() || folderActionLoading.create}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {folderActionLoading.create ? 'Creating...' : 'Add Folder'}
                </button>
              </div>

              {folders.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No custom folders created yet.</p>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {folders.map(folder => (
                    <li key={folder.id} className="py-3 flex items-center justify-between">
                      {editingFolder?.id === folder.id ? (
                        <input
                          type="text"
                          value={editedFolderName}
                          onChange={(e) => setEditedFolderName(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 mr-2"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">{folder.name}</span>
                      )}
                      <div className="flex items-center space-x-2">
                        {editingFolder?.id === folder.id ? (
                          <>
                            <button
                              onClick={() => handleUpdateFolder(folder.id)}
                              disabled={!editedFolderName.trim() || folderActionLoading.update}
                              className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {folderActionLoading.update ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => { setEditingFolder(null); setEditedFolderName(''); }}
                              className="px-3 py-1 bg-gray-300 text-gray-800 rounded-md text-sm hover:bg-gray-400"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditFolderClick(folder)}
                              className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteFolderClick(folder)}
                              className="px-3 py-1 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={folderActionLoading.delete}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Delete Folder Modal */}
      {showDeleteFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b bg-red-50 border-red-200">
              <h3 className="text-lg font-semibold text-red-800">Delete Folder</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the folder "<span className="font-medium">{folderToDelete?.name}</span>"?
                All invoices in this folder will be moved to "Unarchived". This action cannot be undone.
              </p>
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteFolderModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteFolder(folderToDelete.id)}
                  disabled={folderActionLoading.delete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {folderActionLoading.delete ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* JSON Response Modal */}
      {jsonModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className={`px-6 py-4 border-b ${jsonModal.isDevelopmentMode ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-lg font-semibold ${jsonModal.isDevelopmentMode ? 'text-yellow-800' : 'text-blue-800'}`}>
                    {jsonModal.title}
                  </h3>
                  {jsonModal.isDevelopmentMode && (
                    <p className="text-sm text-yellow-700 mt-1">
                      ⚠️ Development Mode - Please update FBR API credentials for real API responses
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setJsonModal({ show: false, title: '', content: '', isDevelopmentMode: false })}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-auto max-h-[70vh]">
              <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-auto whitespace-pre-wrap">
                {jsonModal.content}
              </pre>
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => navigator.clipboard.writeText(jsonModal.content)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Copy JSON
                </button>
                <button
                  onClick={() => setJsonModal({ show: false, title: '', content: '', isDevelopmentMode: false })}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default InvoiceList;
